import React, { KeyboardEvent, useState } from 'react';

import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import { useGetBusinesses } from '~/hooks/apis/business-traveller/useBusinessList';

import { formatDateSlashesWithClock } from '~/services/TimeService';

import BusinessGrid from './BusinessGrid';

const ACTIVE_COLUMNS: Array<GridColDef> = [
  { field: 'id', headerName: 'ID', width: 400, display: 'flex' },
  { field: 'name', headerName: 'Name', width: 250, display: 'flex' },
];

const DEACTIVATED_COLUMNS: Array<GridColDef> = [
  { field: 'id', headerName: 'ID', width: 400, display: 'flex' },
  { field: 'name', headerName: 'Name', width: 250, display: 'flex' },
  {
    field: 'deletedAt',
    headerName: 'Deactivated at',
    width: 200,
    display: 'flex',
    valueFormatter: (value: string) => (value ? formatDateSlashesWithClock(value) : ''),
  },
];

type BusinessStatus = 'active' | 'deactivated';

interface Props {
  status: BusinessStatus;
}

export default function BusinessListPanel({ status }: Props) {
  const [nameString, setNameString] = useState('');
  const [idString, setIdString] = useState('');
  const [filterName, setFilterName] = useState<string | undefined>();
  const [filterId, setFilterId] = useState<string | undefined>();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 50,
    page: 0,
  });

  const { data, isLoading, isError } = useGetBusinesses({
    status,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
    nameString: filterName,
    businessId: filterId,
  });

  function applyFilter() {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setFilterName(nameString || undefined);
    setFilterId(idString || undefined);
  }

  function clearFilters() {
    setNameString('');
    setIdString('');
    setFilterName(undefined);
    setFilterId(undefined);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }

  function handleEnterPress(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter') {
      applyFilter();
    }
  }

  const columns = status === 'deactivated' ? DEACTIVATED_COLUMNS : ACTIVE_COLUMNS;

  return (
    <Stack gap={2}>
      {isError && <Alert severity="error">Error fetching businesses</Alert>}
      <Typography variant="h4">Search for a business</Typography>
      <Stack>
        <Grid container spacing={2}>
          <Stack width="100%">
            <Grid size={12}>
              <TextField
                variant="outlined"
                id="BusinessSearchWithName"
                type="text"
                label="Business search with name"
                placeholder="Enter Business Name"
                onChange={(e) => setNameString(e.target.value)}
                value={nameString}
                fullWidth
                onKeyDown={handleEnterPress}
              />
            </Grid>
            <Grid size={12}>- OR -</Grid>
            <Grid size={12}>
              <TextField
                variant="outlined"
                id="BusinessSearchWithId"
                type="text"
                label="Business search with Id"
                placeholder="Enter Business Id"
                onChange={(e) => setIdString(e.target.value)}
                value={idString}
                fullWidth
                onKeyDown={handleEnterPress}
              />
            </Grid>
            <Stack gap={2} direction="row" justifyContent="end">
              <Button variant="text" onClick={clearFilters}>
                Clear all filters
              </Button>
              <Button variant="contained" onClick={applyFilter}>
                FILTER
              </Button>
            </Stack>
          </Stack>
          <BusinessGrid
            columns={columns}
            loading={isLoading}
            onPaginationModelChange={setPaginationModel}
            paginationModel={paginationModel}
            rows={data?.result ?? []}
            total={data?.meta?.total ?? 0}
          />
        </Grid>
      </Stack>
    </Stack>
  );
}
